// Copyright 2025-2026 Florian Gallon. All Rights reserved.
// Ingredient service utility for Firestore-backed ingredient management

import { db } from '../firebase';
import {
  collection,
  doc,
  setDoc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDoc,
  getDocs,
  query,
  where,
  limit,
  onSnapshot
} from 'firebase/firestore';
import { INGREDIENT_LIBRARY, INGREDIENT_TRANSLATIONS } from '../constants/ingredients';
import { t } from '../translations';

/**
 * Subscribe to real-time ingredient updates for a bakery
 * @param {string} bakeryId - The bakery ID
 * @param {function} callback - Callback function receiving (ingredientsMap, ingredientsList)
 * @returns {function} Unsubscribe function
 */
export function subscribeIngredients(bakeryId, callback) {
  const q = query(
    collection(db, 'ingredients'),
    where('bakeryId', '==', bakeryId)
  );

  return onSnapshot(q, (snapshot) => {
    const ingredientsList = [];
    const ingredientsByKey = new Map();

    snapshot.forEach((doc) => {
      const data = { id: doc.id, ...doc.data() };
      ingredientsList.push(data);
      if (data.key) {
        ingredientsByKey.set(data.key, data);
      }
    });

    // Sort by name (system first, then custom)
    ingredientsList.sort((a, b) => {
      // System ingredients first
      if (a.isSystem && !b.isSystem) return -1;
      if (!a.isSystem && b.isSystem) return 1;
      // Then alphabetically by English name
      const nameA = a.name?.en || a.key || '';
      const nameB = b.name?.en || b.key || '';
      return nameA.localeCompare(nameB);
    });

    callback(ingredientsByKey, ingredientsList);
  });
}

/**
 * Get a single ingredient by key
 * @param {string} bakeryId - The bakery ID
 * @param {string} key - The ingredient key
 * @returns {Promise<object|null>} The ingredient document or null
 */
export async function getIngredientByKey(bakeryId, key) {
  // For system ingredients, docId = key
  if (!key.startsWith('custom_')) {
    const docRef = doc(db, 'ingredients', key);
    const docSnap = await getDoc(docRef);
    if (docSnap.exists() && docSnap.data().bakeryId === bakeryId) {
      return { id: docSnap.id, ...docSnap.data() };
    }
  }

  // For custom ingredients, query by key field
  const q = query(
    collection(db, 'ingredients'),
    where('bakeryId', '==', bakeryId),
    where('key', '==', key)
  );
  const snapshot = await getDocs(q);
  if (!snapshot.empty) {
    const doc = snapshot.docs[0];
    return { id: doc.id, ...doc.data() };
  }

  return null;
}

/**
 * Get display label for an ingredient
 * @param {string} keyOrString - Ingredient key or legacy string
 * @param {Map} ingredientsByKey - Map of key -> ingredient doc
 * @param {string} language - 'en' or 'fr'
 * @returns {string} Display label
 */
export function getIngredientLabel(keyOrString, ingredientsByKey, language) {
  if (!keyOrString) return '';

  const doc = ingredientsByKey.get(keyOrString);
  if (doc?.name) {
    return doc.name[language] || doc.name.en || doc.name.fr || keyOrString;
  }

  // Custom key whose ingredient doc was deleted: don't show the raw Firestore id
  if (keyOrString.startsWith('custom_')) {
    return t('deletedIngredient', language);
  }

  // Fallback: return the raw string (legacy custom ingredient)
  return keyOrString;
}

/**
 * Ensure system ingredients are seeded for a bakery (idempotent)
 * Uses setDoc with merge to safely handle concurrent calls
 * @param {string} bakeryId - The bakery ID
 * @returns {Promise<void>}
 */
export async function ensureSeededIngredients(bakeryId, { force = false } = {}) {
  if (!force) {
    // Skip if the bakery already has ingredients — prevents re-seeding after intentional deletion
    const existingSnap = await getDocs(
      query(collection(db, 'ingredients'), where('bakeryId', '==', bakeryId), limit(1))
    );
    if (!existingSnap.empty) return;
  }

  const now = new Date().toISOString();

  // Use batched writes for efficiency, but setDoc with merge for idempotency
  const promises = INGREDIENT_LIBRARY.map(async (key) => {
    const docRef = doc(db, 'ingredients', `${bakeryId}_${key}`);
    const docSnap = await getDoc(docRef);

    // Only create if doesn't exist (check first to avoid unnecessary writes)
    if (!docSnap.exists()) {
      await setDoc(docRef, {
        bakeryId,
        key,
        name: {
          en: INGREDIENT_TRANSLATIONS.en[key] || key,
          fr: INGREDIENT_TRANSLATIONS.fr[key] || key
        },
        isAllergen: false,
        allergenName: { en: '', fr: '' },
        isSystem: true,
        createdAt: now,
        updatedAt: now
      }, { merge: true });
    }
  });

  await Promise.all(promises);
}

/**
 * Create a custom ingredient
 * @param {string} bakeryId - The bakery ID
 * @param {string} inputName - The ingredient name (will be copied to both en/fr)
 * @param {string} currentLanguage - Current language ('en' or 'fr')
 * @returns {Promise<string>} The new ingredient key
 */
export async function createCustomIngredient(bakeryId, inputName, currentLanguage) {
  const now = new Date().toISOString();

  // Create doc first to get the auto-generated ID
  const docRef = await addDoc(collection(db, 'ingredients'), {
    bakeryId,
    key: '', // Placeholder, will update
    name: { en: inputName, fr: inputName }, // Copy to both
    isAllergen: false,
    allergenName: { en: '', fr: '' },
    isSystem: false,
    createdAt: now,
    updatedAt: now
  });

  // Update with key = custom_<docId>
  const key = `custom_${docRef.id}`;
  await updateDoc(docRef, { key, updatedAt: new Date().toISOString() });

  return key;
}

/**
 * Update an ingredient (allergen flag, translations, etc.)
 * @param {string} ingredientId - The Firestore document ID
 * @param {object} updates - Fields to update
 * @returns {Promise<void>}
 */
export async function updateIngredient(ingredientId, updates) {
  const docRef = doc(db, 'ingredients', ingredientId);
  await updateDoc(docRef, {
    ...updates,
    updatedAt: new Date().toISOString()
  });
}

/**
 * Delete a custom ingredient (only non-system ingredients can be deleted)
 * @param {string} ingredientId - The Firestore document ID
 * @returns {Promise<boolean>} True if deleted, false if system ingredient
 */
export async function deleteIngredient(ingredientId) {
  const docRef = doc(db, 'ingredients', ingredientId);
  const docSnap = await getDoc(docRef);

  if (!docSnap.exists()) {
    return false;
  }

  await deleteDoc(docRef);
  return true;
}

/**
 * Check if ingredients have been seeded for a bakery
 * @param {string} bakeryId - The bakery ID
 * @returns {Promise<boolean>}
 */
export async function checkIngredientsSeeded(bakeryId) {
  // Check if at least one system ingredient exists
  const testKey = INGREDIENT_LIBRARY[0];
  const docRef = doc(db, 'ingredients', `${bakeryId}_${testKey}`);
  const docSnap = await getDoc(docRef);
  return docSnap.exists();
}

/**
 * Check if an ingredient has any stock purchase records (blocks deletion)
 * Supports hybrid matching: ingredientKey (new) OR ingredientName (legacy)
 * @param {string} bakeryId - The bakery ID
 * @param {string} ingredientKey - The ingredient key
 * @returns {Promise<boolean>}
 */
export async function checkIngredientHasStockPurchases(bakeryId, ingredientKey) {
  const q1 = query(
    collection(db, 'stockPurchases'),
    where('bakeryId', '==', bakeryId),
    where('ingredientKey', '==', ingredientKey)
  );
  const snap1 = await getDocs(q1);
  if (!snap1.empty) return true;

  const q2 = query(
    collection(db, 'stockPurchases'),
    where('bakeryId', '==', bakeryId),
    where('ingredientName', '==', ingredientKey)
  );
  const snap2 = await getDocs(q2);
  return !snap2.empty;
}

/**
 * Build ingredients map from list (helper function)
 * @param {Array} ingredientsList - List of ingredient documents
 * @returns {Map} Map of key -> ingredient doc
 */
export function buildIngredientsMap(ingredientsList) {
  const map = new Map();
  ingredientsList.forEach((ing) => {
    if (ing.key) {
      map.set(ing.key, ing);
    }
  });
  return map;
}
